import { Instant, IsoFields, ZonedDateTime, ZoneOffset } from "@js-joda/core";
import { useGetDataInfoRange } from "api/api-hooks/datasets-hooks";
import { InstantDoubleValue } from "api/model/dataset/InstantDoubleValue";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { useComponentStateCallback } from "hooks/dashboard/useComponentStateCallback";
import useTimeState from "hooks/dashboard/useTimeState";
import { useCompanyTimezone } from "hooks/useTimezone";
import { Layout } from "plotly.js";
import React, { useEffect } from "react";
import Plot from "react-plotly.js";
import { T } from "services/lang/LanguageService";
import { DATE_PATTERN_SECONDS } from "utils/constants";
import { ComponentTheme, DweenTimeUnitEnum } from "utils/enums";
import { getAdjustedReferenceTime } from "utils/four-four-five-calendar-utils";
import { fmtDatetime, getComponentRefTime, toZonedDateTime, toZonedDateTimeOrNow } from "utils/time-utils";
import { PlotDataEx } from "../charts/interfaces";
import {
  aggregateValues,
  daysArrayShort,
  defaultFromDateTime,
  defaultToDateTime,
  getDweenColor,
  getLegendGroup,
  getTextColor,
  getWeekRange,
  getXAxisLabel,
  monthsArrayShort,
} from "../charts/utils";
import { defaultLoadConf } from "../data-input-output-table/defaultConfigs";
import { AggregationOption } from "../product-template-bar-chart/product-template-bar-chart-editor/ProductTemplateBarChartTypes";
import "./PeriodicDataChart.scss";
import {
  AggregatedEntry,
  ChartType,
  ComparisonPeriod,
  DateRangeSelectType,
  PeriodicDataChartProps,
} from "./PeriodicDataChartTypes";

const getPeriodicDataChartLayout = (
  sortedXLabels: string[],
  theme?: ComponentTheme,
  reportMode?: boolean,
  hideLegend?: boolean,
  margin?: { t?: number; b?: number; l?: number; r?: number }
) => {
  return {
    font: {
      color: getTextColor(theme),
    },
    paper_bgcolor: "transparent",
    plot_bgcolor: "transparent",
    autosize: true,
    showlegend: !hideLegend,
    yaxis: {
      tickfont: {
        color: getTextColor(theme),
      },
    },
    xaxis: {
      type: "category",
      categoryorder: "array",
      categoryarray: sortedXLabels,
      tickfont: {
        size: reportMode ? 10 : 14,
        color: getTextColor(theme),
        family: "Arial Narrow, sans-serif",
        weight: 600,
      },
    },
    barmode: "group",
    margin: { t: margin?.t || 15, b: margin?.b || null, l: margin?.l || 30, r: margin?.r || 30 },
  };
};

export default function PeriodicDataChart(props: PeriodicDataChartProps) {
  const isNoComparison = props.comparisonPeriod === ComparisonPeriod.NO_COMPARISON;
  const companyTz = useCompanyTimezone();
  const displayTz = useCompanyTimezone();

  const setStatus = useComponentStateCallback(props);

  const timeState = useTimeState({
    from: props.from ?? props.dateFrom,
    to: props.to ?? props.dateTo,
  });

  const initialReferenceTime = getComponentRefTime(props);
  const refTime = getAdjustedReferenceTime(
    initialReferenceTime,
    companyTz,
    props.calendarType,
    false,
    props.useDataUpToLastFinishedWeek
  );

  const useLoadConfFallback =
    !props.dateRangeSelectType || props.dateRangeSelectType === DateRangeSelectType.LOAD_CONF;

  const defaultFromDate = useLoadConfFallback
    ? defaultFromDateTime({
        zoneId: companyTz,
        loadConf: { ...defaultLoadConf, ...props.loadConf },
        referenceTime: refTime,
      })
    : timeState.from;

  let defaultToDate = useLoadConfFallback
    ? defaultToDateTime({
        zoneId: companyTz,
        loadConf: {
          ...defaultLoadConf,
          ...props.loadConf,
          alignUnit: props.useDataUpToLastFinishedMonth
            ? DweenTimeUnitEnum.MONTHS
            : props.loadConf?.alignUnit,
          leadUnit: props.useDataUpToLastFinishedMonth ? DweenTimeUnitEnum.MINUTES : props.loadConf?.leadUnit,
          lead: props.useDataUpToLastFinishedMonth ? 0 : props.loadConf?.lead,
        },
        referenceTime: refTime,
      })
    : timeState.to;

  if (props.useDataUpToLastFinishedMonth && defaultToDate) {
    const zdt = toZonedDateTime(defaultToDate, companyTz);
    defaultToDate = new Date(zdt.minusMinutes(2).toInstant().toEpochMilli());
  }

  const {
    data: dataRange,
    isLoading,
    error,
  } = useGetDataInfoRange(
    [props.valueDataset],
    {
      timeStart: defaultFromDate?.toISOString()!,
      timeEnd: defaultToDate?.toISOString(),
    },
    {
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 0,
      enabled: !!props.valueDataset && !!defaultFromDate && !!defaultToDate,
    }
  );

  useEffect(() => {
    if (!isLoading && dataRange !== undefined) {
      setStatus({ state: "finished" });
    }
  }, [isLoading, dataRange, setStatus]);

  if (error) {
    easasAlert({
      summary: T("alert.failed-to-fetch-data"),
      detail: error.message,
      severity: "error",
    });
    setStatus({ state: "error", error: error.message });
  }

  const rawData = dataRange?.[0]?.data ?? [];

  const groupedData = new Map<string, Map<string, AggregatedEntry>>();
  const xLabels = new Set<string>();
  const legendMeta = new Map<string, { startYear: number; endYear: number; displayName: string }>();
  const ref = toZonedDateTimeOrNow(companyTz, refTime);
  let currentYear = ref.year();
  const currentWeek = ref.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR);
  const currentMonth = ref.monthValue();
  const currentDay = ref.dayOfWeek().ordinal();

  if (currentWeek === 1 && currentMonth === 12 && props.comparisonPeriod === ComparisonPeriod.WEEKLY_YEARLY) {
    currentYear += 1;
  }

  // Precompute base week window for WEEKLY_YEARLY so we can assign points to windows like 2025-2026, 2024-2025, ...
  let baseWeeks: { week: number; year: number }[] = [];
  let baseStartYear = 0;
  let baseEndYear = 0;
  if (props.comparisonPeriod === ComparisonPeriod.WEEKLY_YEARLY) {
    baseWeeks = getWeekRange(
      props.weeksRangeStartOffset ?? 3,
      props.weeksRangeEndOffset ?? 3,
      companyTz,
      refTime
    );
    if (baseWeeks.length > 0) {
      baseStartYear = baseWeeks[0].year;
      baseEndYear = baseWeeks.at(-1)?.year!;
    }
  }

  rawData.forEach(point => {
    const instant = Instant.parse(point.key);
    let xKey: string;
    if (props.comparisonPeriod === ComparisonPeriod.DAILY_WEEKLY) {
      const zdt = ZonedDateTime.ofInstant(instant, ZoneOffset.UTC);
      xKey = daysArrayShort[zdt.dayOfWeek().ordinal()];
    } else {
      xKey = getXAxisLabel(instant, props.comparisonPeriod, displayTz);
    }

    let legendKey: string = getLegendGroup(instant, props.comparisonPeriod, companyTz);

    // For weekly-yearly we want a window-based grouping (e.g., 2025-2026, 2024-2025)
    if (
      props.groupingMode === "CONTINUOUS" &&
      props.comparisonPeriod === ComparisonPeriod.WEEKLY_YEARLY &&
      baseWeeks &&
      baseWeeks.length > 0
    ) {
      const zdt = instant.atZone(companyTz);
      const wk = zdt.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR);
      const month = zdt.monthValue();
      const yr = wk === 1 && month === 12 ? zdt.year() + 1 : zdt.year();

      const matching = baseWeeks.find(b => b.week === wk);
      if (!matching) return;
      const n = matching.year - yr; // how many years older than base
      const startYear = baseStartYear - n;
      const endYear = baseEndYear - n;
      legendKey = `${startYear}-${endYear}`;

      if (!legendMeta.has(legendKey)) {
        const display = startYear === endYear ? `${startYear}` : `${startYear}-${endYear}`;
        legendMeta.set(legendKey, { startYear, endYear, displayName: display });
      }
    }

    xLabels.add(xKey);

    if (!groupedData.has(legendKey)) groupedData.set(legendKey, new Map());
    const group = groupedData.get(legendKey)!;
    if (!group.has(xKey)) {
      group.set(xKey, {
        total: 0,
        count: 0,
        min: Number.POSITIVE_INFINITY,
        max: Number.NEGATIVE_INFINITY,
      });
    }

    const entry = group.get(xKey)!;
    entry.total += point.value ?? 0;
    entry.count += 1;
    entry.min = Math.min(entry.min, point.value ?? 0);
    entry.max = Math.max(entry.max, point.value ?? 0);
  });

  const getSortedXLabels = (currentData?: InstantDoubleValue[]) => {
    if (props.comparisonPeriod === ComparisonPeriod.WEEKLY_YEARLY) {
      return baseWeeks.map(w => `W${w.week}`);
    }

    if (props.comparisonPeriod === ComparisonPeriod.MONTHLY_YEARLY || props.comparisonPeriod === undefined) {
      return monthsArrayShort;
    }
    if (props.comparisonPeriod === ComparisonPeriod.DAILY_WEEKLY) {
      return daysArrayShort;
    }
    if (isNoComparison) {
      return currentData?.map(v => fmtDatetime(v.key, displayTz, DATE_PATTERN_SECONDS)) ?? [];
    }
    return Array.from(xLabels).sort();
  };

  const getLegendStartYear = (legend: string) => {
    if (!legend) return NaN;
    const parts = legend.split("-");
    const n = parseInt(parts[0], 10);
    return Number.isFinite(n) ? n : NaN;
  };

  const allGroups = Array.from(groupedData.keys()).sort((a, b) => {
    const aStart = getLegendStartYear(a);
    const bStart = getLegendStartYear(b);
    if (Number.isFinite(aStart) && Number.isFinite(bStart)) return bStart - aStart;
    return a.localeCompare(b);
  });

  const sortedXLabels = getSortedXLabels(dataRange?.[0]?.data ?? []);

  const plotData = allGroups.map((legendKey, index) => {
    const group = groupedData.get(legendKey)!;
    const simpleData = isNoComparison
      ? rawData.map(p => ({
          x: toZonedDateTime(p.key, displayTz).toInstant().toString(),
          y: p.value ?? null,
        }))
      : undefined;

    const meta = legendMeta.get(legendKey);
    const displayName = meta?.displayName ?? legendKey;

    return {
      type: props.chartType ?? ChartType.LINE,
      mode: "lines+markers",
      name: displayName,
      x: isNoComparison ? simpleData?.map(d => d.x) : sortedXLabels,
      y: isNoComparison
        ? simpleData?.map(p => p.y)
        : sortedXLabels.map(label => {
            const entry = group.get(label);

            // determine if this legend corresponds to the current window/year range
            let sYear = NaN;
            let eYear = NaN;
            const parts = legendKey.split("-");
            if (parts.length === 1) {
              sYear = parseInt(parts[0], 10);
              eYear = sYear;
            } else {
              sYear = parseInt(parts[0], 10);
              eYear = parseInt(parts[1], 10);
            }
            const isCurrentLegend =
              Number.isFinite(sYear) &&
              Number.isFinite(eYear) &&
              currentYear >= sYear &&
              currentYear <= eYear;

            if (props.hideFutureValues && isCurrentLegend) {
              if (props.comparisonPeriod === ComparisonPeriod.WEEKLY_YEARLY) {
                const weekNum = parseInt(label.replace("W", ""), 10);

                if (baseWeeks.findIndex(bw => bw.week === weekNum) === -1) {
                  return null;
                }
              }

              if (props.comparisonPeriod === ComparisonPeriod.MONTHLY_YEARLY) {
                const monthIndex = monthsArrayShort.indexOf(label) + 1;
                if (monthIndex > currentMonth) {
                  return null;
                }
              }

              if (props.comparisonPeriod === ComparisonPeriod.DAILY_WEEKLY) {
                const dayIndex = daysArrayShort.indexOf(label);
                if (dayIndex > currentDay) {
                  return null;
                }
              }
            }

            return entry ? aggregateValues(entry, props.aggregationType ?? AggregationOption.SUM) : null;
          }),
      marker: {
        color: getDweenColor(isNoComparison ? 1 : index, props.theme),
      },
    };
  });

  return (
    <div
      style={{
        width: props.width ?? "100%",
        height: props.height ?? "100%",
      }}
      className={`periodic-bar-chart${props.reportMode ? "-report-mode" : ""}`}
    >
      <div className="subtitle" style={{ color: getTextColor(props.theme) }}>
        {props.subtitle}
      </div>
      <div className="title" style={{ color: getTextColor(props.theme) }}>
        {props.title}
      </div>

      <Plot
        data={plotData as PlotDataEx[]}
        useResizeHandler
        className="w-full h-full"
        layout={{
          ...(getPeriodicDataChartLayout(
            isNoComparison ? [] : getSortedXLabels(),
            props.theme,
            props.reportMode,
            props.hideLegend,
            props.chartMargin
          ) as Partial<Layout>),
          ...(isNoComparison && {
            xaxis: {
              type: "date",
              tickformat: "%b %d",
            },
          }),
        }}
        config={{ displayModeBar: false }}
      />
    </div>
  );
}
